/**
 * NASA EONET (Earth Observatory Natural Event Tracker) data source.
 * Fetches active natural events: wildfires, storms, volcanic eruptions,
 * floods, icebergs, and other environmental hazards.
 * Free, no API key required. Public domain.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'eonet';

/** Map EONET category IDs to our event types and labels. */
const CATEGORY_MAP: Record<string, { type: NormalizedEvent['type']; label: string }> = {
  wildfires:     { type: 'fire',     label: 'Wildfire' },
  severeStorms:  { type: 'weather',  label: 'Severe Storm' },
  volcanoes:     { type: 'disaster', label: 'Volcanic Activity' },
  earthquakes:   { type: 'earthquake', label: 'Earthquake' },
  floods:        { type: 'disaster', label: 'Flood' },
  landslides:    { type: 'disaster', label: 'Landslide' },
  seaLakeIce:    { type: 'disaster', label: 'Sea/Lake Ice' },
  drought:       { type: 'weather',  label: 'Drought' },
  dustHaze:      { type: 'weather',  label: 'Dust/Haze' },
  snow:          { type: 'weather',  label: 'Snow' },
  tempExtremes:  { type: 'weather',  label: 'Temperature Extreme' },
  waterColor:    { type: 'disaster', label: 'Water Discoloration' },
  manmade:       { type: 'disaster', label: 'Manmade Event' },
};

function categoryToSeverity(categoryId: string): Severity {
  switch (categoryId) {
    case 'volcanoes':
    case 'earthquakes': return 'high';
    case 'wildfires':
    case 'severeStorms':
    case 'floods':
    case 'landslides': return 'medium';
    default: return 'low';
  }
}

interface EonetGeometry {
  date: string;
  type: string;
  coordinates: number[] | number[][];
}

interface EonetCategory {
  id: string;
  title: string;
}

interface EonetEvent {
  id: string;
  title: string;
  description?: string;
  link: string;
  closed?: string;
  categories: EonetCategory[];
  geometry: EonetGeometry[];
}

interface EonetResponse {
  events?: EonetEvent[];
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const eonetSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'NASA EONET',
    description: 'Earth Observatory Natural Event Tracker — wildfires, storms, volcanic eruptions',
    icon: 'cloud-lightning',
    category: 'environmental',
    updateInterval: 1_800_000, // 30 min
    requiresApiKey: false,
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] NASA EONET source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/eonet?days=7&status=open`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`EONET API returned ${response.status}`);
      }

      const data: EonetResponse = await response.json();

      if (!data.events || !Array.isArray(data.events)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const eonetEvent of data.events) {
        if (!eonetEvent.geometry || eonetEvent.geometry.length === 0) continue;

        const categoryId = eonetEvent.categories[0]?.id ?? 'unknown';
        const mapped = CATEGORY_MAP[categoryId] ?? { type: 'disaster' as const, label: 'Natural Event' };

        // Use the most recent geometry point
        const latestGeo = eonetEvent.geometry[eonetEvent.geometry.length - 1];
        const coords = latestGeo.coordinates;

        // EONET coordinates are [lng, lat] for points, or [[lng, lat], ...] for polygons
        let lng: number;
        let lat: number;

        if (Array.isArray(coords[0])) {
          // Polygon — use first point
          const first = coords[0] as number[];
          lng = first[0];
          lat = first[1];
        } else {
          lng = coords[0] as number;
          lat = coords[1] as number;
        }

        if (!isFinite(lat) || !isFinite(lng)) continue;

        const timestamp = latestGeo.date ?? new Date().toISOString();

        events.push({
          id: `eonet-${eonetEvent.id}`,
          source: SOURCE_ID,
          timestamp,
          type: mapped.type,
          title: eonetEvent.title,
          description: eonetEvent.description ?? undefined,
          severity: categoryToSeverity(categoryId),
          location: {
            lat,
            lng,
            name: eonetEvent.title,
          },
          tags: ['eonet', categoryId, mapped.label.toLowerCase().replace(/\s+/g, '-')],
          links: eonetEvent.link
            ? [{ url: eonetEvent.link, label: 'NASA EONET' }]
            : [],
          raw: {
            eonetId: eonetEvent.id,
            categoryId,
            categoryTitle: eonetEvent.categories[0]?.title,
            closed: eonetEvent.closed ?? null,
            geometryCount: eonetEvent.geometry.length,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
